import { equals, path, pathOr, pipe } from 'ramda'

import { isTempId } from 'utils'

import {
  getReachableRulesAndGroups,
  getTreeHasCycle,
  routingRuleIdField,
  translateRulesAndGroupsToMagnusNodes,
} from './helpers'

const isNotNew = (ruleOrGroup) => !isTempId(ruleOrGroup.id)

const root = (state) => state.routing

const orgRoot = (state) => state.org

const uiRoot = (state) => root(state).ui
export const isDraftSaving = (state) => uiRoot(state).saving
export const savingError = (state) => uiRoot(state).savingError
export const showingPublished = (state) => uiRoot(state).showingPublished
export const isSelectingContinueToGroup = (state) =>
  uiRoot(state).selectingContinueToGroup.showing
export const isShowingAddNewRule = (state) =>
  uiRoot(state).addingNewRule.showing
export const addNewRuleId = (state) => uiRoot(state).addingNewRule.forRuleId
export const isShowingEditAction = (state) =>
  uiRoot(state).editingAction.showing
export const editActionRuleId = (state) => uiRoot(state).editingAction.forRuleId

export const hasOptedIntoNewFallbacksFeature = (state) => {
  const hasOptIn = pathOr(
    false,
    ['properties', 'hasOptedIntoRoutingFallbackFeature'],
    orgRoot(state)
  )
  const isOptInFlowHidden = pathOr(
    false,
    ['hideFallbackOptInFlow'],
    uiRoot(state)
  )
  return hasOptIn || isOptInFlowHidden
}

export const publishedRoot = (state) => root(state).published
const publishedRules = (state) => publishedRoot(state).rules
const getPublishedRule = (state, ruleId) =>
  publishedRules(state).find((rule) => rule.id === ruleId)
export const rawPublishedRuleGroups = (state) => publishedRoot(state).ruleGroups
export const hydratedPublishedRuleGroups = (state) => {
  const groups = publishedRoot(state).ruleGroups
  return groups.map((group) => ({
    ...group,
    rules: group[routingRuleIdField()].map((ruleId) =>
      getPublishedRule(state, ruleId)
    ),
  }))
}

export const getRootGroup = (state) => {
  const tree = publishedRoot(state).ruleTree
  return hydratedPublishedRuleGroups(state).find(
    ({ id }) => id === tree.rootRuleGroupId
  )
}

export const translatedPublished = (state) => {
  const allRules = publishedRules(state)
  const allGroups = hydratedPublishedRuleGroups(state)
  return translateRulesAndGroupsToMagnusNodes({
    allRules,
    allGroups,
  })
}

export const draftRoot = (state) => root(state).draft
export const draftIsDirty = (state) => {
  const published = publishedRoot(state)
  const draft = draftRoot(state)

  const ruleIsDeleted = (rule) =>
    draft.deletedRules.map((rule) => rule.id).includes(rule.id)

  // any deleted rules or groups?
  if (
    draft.deletedRules.filter(isNotNew).length ||
    draft.deletedRuleGroups.filter(isNotNew).length
  ) {
    return true
  }

  // any new rules or groups?
  if (
    draft.rules.some((rule) => isTempId(rule.id) && !ruleIsDeleted(rule)) ||
    draft.ruleGroups.some((group) => isTempId(group.id))
  ) {
    return true
  }

  // any changed rules or groups?
  if (
    published.rules.some(
      (publishedRule) =>
        !equals(publishedRule, draft.rulesById[publishedRule.id])
    ) ||
    published.ruleGroups.some(
      (publishedGroup) =>
        !equals(publishedGroup, draft.ruleGroupsById[publishedGroup.id])
    )
  ) {
    return true
  }

  // was the default rule changed?
  if (
    !equals(published.defaultRuleUsers.sort(), draft.defaultRuleUsers.sort())
  ) {
    return true
  }

  return false
}
const draftRules = (state) => draftRoot(state).rules
const draftRuleGroups = (state) => draftRoot(state).ruleGroups
const deletedRules = (state) => draftRoot(state).deletedRules
export const getDraftRuleGroup = (state, groupId) =>
  draftRoot(state).ruleGroupsById[groupId]
export const getDraftDefaultUsers = (state) => draftRoot(state).defaultRuleUsers
export const getDraftRule = (state, ruleId) =>
  draftRoot(state).rulesById[ruleId]
export const getDraftRuleOrDefaultGroupAction = (
  state,
  ruleId,
  isDefaultForGroup,
  groupId
) => {
  if (ruleId && !isDefaultForGroup) {
    return getDraftRule(state, ruleId)
  } else {
    const draftRuleGroup = getDraftRuleGroup(state, groupId)
    return { action: draftRuleGroup ? draftRuleGroup.defaultAction : undefined }
  }
}
export const getDraftDefaultGroupAction = (state, groupId) =>
  getDraftRuleGroup(state, groupId)
    ? getDraftRuleGroup(state, groupId).defaultAction
    : null
export const rawDraftRuleGroups = (state) => draftRoot(state).ruleGroups
export const hydratedDraftRuleGroups = (state) => {
  const groups = draftRoot(state).ruleGroups
  return groups.map((group) => ({
    ...group,
    rules: group[routingRuleIdField()].map((ruleId) =>
      getDraftRule(state, ruleId)
    ),
  }))
}
export const ruleWasDeleted = (state, ruleId) =>
  !!draftRoot(state).deletedRules.filter((rule) => rule.id === ruleId).length

const ruleHasUsers = (rule) => {
  if (rule.action && rule.action.type !== 'PARTICIPANT') {
    return true
  }

  const singleUser = path(['action', 'options', 'userId'], rule)
  const userGroup = path(['action', 'options', 'userIds'], rule)
  const teamId = path(['action', 'options', 'teamId'], rule)

  return singleUser || (userGroup && !equals(userGroup, [])) || teamId
}
const ruleHasError = (rule) =>
  !rule.conditions ||
  !rule.action ||
  !rule.action.type ||
  !rule.action.options ||
  !ruleHasUsers(rule)

const groupHasError = (group) =>
  !group[routingRuleIdField()] ||
  !group.defaultAction ||
  !group.defaultAction.type ||
  !group.defaultAction.options

export const anyRuleOrGroupHasError = (state) => {
  const reachableRulesAndGroups = getReachableRulesAndGroups(
    draftRules(state),
    draftRuleGroups(state),
    getRootGroup(state).id
  )
  return (
    reachableRulesAndGroups.rules.some(ruleHasError) ||
    reachableRulesAndGroups.groups.some(groupHasError)
  )
}

const ruleErrorToMessage = (rule) => {
  const ruleErrorTests = [
    (test) =>
      !test.rule.conditions
        ? { ...test, messages: [...test.messages, 'No conditions'] }
        : test,
    (test) =>
      !test.rule.action
        ? { ...test, messages: [...test.messages, 'No action'] }
        : test,
    (test) =>
      !path(['action', 'type'], test.rule)
        ? { ...test, messages: [...test.messages, 'No action type'] }
        : test,
    (test) =>
      !path(['action', 'options'], test.rule)
        ? { ...test, messages: [...test.messages, 'No action options'] }
        : test,
    (test) =>
      !ruleHasUsers(test.rule)
        ? { ...test, messages: [...test.messages, 'No users'] }
        : test,
  ]

  const { messages } = pipe.apply(null, ruleErrorTests)({ rule, messages: [] })
  return messages
}

const groupErrorToMessage = (group) => {
  const groupErrorTests = [
    (test) =>
      !test.group[routingRuleIdField()]
        ? { ...test, messages: [...test.messages, 'No rules'] }
        : test,
    (test) =>
      !test.group.defaultAction
        ? { ...test, messages: [...test.messages, 'No default action'] }
        : test,
    (test) =>
      !path(['defaultAction', 'type'], test.group)
        ? { ...test, messages: [...test.messages, 'No default action type'] }
        : test,
    (test) =>
      !path(['defaultAction', 'options'], test.group)
        ? { ...test, messages: [...test.messages, 'No default action options'] }
        : test,
  ]

  const { messages } = pipe.apply(
    null,
    groupErrorTests
  )({ group, messages: [] })
  return messages
}

export const getErrors = (state) => {
  const reachableRulesAndGroups = getReachableRulesAndGroups(
    draftRules(state),
    draftRuleGroups(state),
    getRootGroup(state).id
  )

  const ruleErrors = (rules) => (errors) => {
    const errs = rules
      .map((rule) => ({
        ruleId: rule.id,
        ruleName: rule.name,
        messages: ruleErrorToMessage(rule),
      }))
      .filter((e) => e.messages.length > 0)

    return [...errs, ...errors]
  }

  const groupErrors = (groups) => (errors) => {
    const errs = groups
      .map((group) => ({
        groupPlace: group.place,
        messages: groupErrorToMessage(group),
      }))
      .filter((e) => e.messages.length > 0)

    return [...errs, ...errors]
  }

  return pipe(
    ruleErrors(reachableRulesAndGroups.rules),
    groupErrors(reachableRulesAndGroups.groups)
  )([])
}

export const treeHasCycle = (state) => {
  return getTreeHasCycle(
    draftRules(state),
    draftRuleGroups(state),
    getRootGroup(state).id
  )
}

export const translatedDraft = (state) => {
  const allRules = draftRules(state)
  const allGroups = hydratedDraftRuleGroups(state)
  return translateRulesAndGroupsToMagnusNodes({
    allRules,
    allGroups,
  })
}

export const getTeam = (state, teamId) =>
  root(state).teams.find((team) => team.id === teamId)

const ruleIsDeleted = (state, rule) =>
  deletedRules(state)
    .map((rule) => rule.id)
    .includes(rule.id)

export const getRulesByType = (state, type) =>
  draftRules(state).filter(
    (rule) =>
      equals(rule.status, 'ENABLED') &&
      !ruleIsDeleted(state, rule) &&
      equals(path(['action', 'type'], rule), type)
  )
